import { createFileRoute, Link, Outlet, useMatchRoute, useNavigate } from '@tanstack/react-router'
import type { TeamMemberData } from '@workspace/api-backend'
import type {
  ProfileEditFormActions,
  ProfileEditFormData,
} from '@workspace/seedforge-features/profile'
import { ProfileEditForm, ProfileView } from '@workspace/seedforge-features/profile'
import type { ShareLinkConfig } from '@workspace/seedforge-features/share'
import { isDeckShared, ShareLinks } from '@workspace/seedforge-features/share'
import {
  FOUNDER_NAV_INVESTOR_FEEDBACK_LABEL,
  FOUNDER_NAV_SHARE_ANALYTICS_LABEL,
  FOUNDER_NAV_SHARE_PROFILE_LABEL,
} from '@workspace/seedforge-ui/lib/founder-nav'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu'
import { CtaButton, ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { SpinnerRow } from '@workspace/ui/components/spinner-row'
import { formatFileSize, getInitials } from '@workspace/ui/lib/utils'

type DataRoomTab = 'profile' | 'links' | 'deep-insights' | 'analytics'

import { FileUp, Pencil, Share2, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FounderLayout } from '../../../../components/founder-layout'
import { ProfileCompletenessCard } from '../../../../components/profile-completeness-card'
import { VideoPreviewModal } from '../../../../components/video-preview-modal'
import { useStartupContext } from '../../../../contexts/startup-guard'
import { DeepInsightsPanel } from '../../../../features/analytics/components/deep-insights-panel'
import type { ProfileChatQuestionInput } from '../../../../features/ask-profile'
import { AskProfileProvider, AskProfileSection } from '../../../../features/ask-profile'
import { useDeckDrop } from '../../../../features/home/hooks/use-deck-drop'
import { AiReadsPanel } from '../../../../features/investor-signals/components/ai-reads-panel'
import { PostImprovementPromptOverlay } from '../../../../features/post-improvement-prompt/components/post-improvement-prompt-overlay'
import { usePostImprovementPrompt } from '../../../../features/post-improvement-prompt/hooks/use-post-improvement-prompt'
import { DocumentPreviewModal } from '../../../../features/preview/components/document-preview-modal'
import { InlineDocumentPreview } from '../../../../features/preview/components/inline-document-preview'
import { FeedbackHintBar } from '../../../../features/share-analytics/components/feedback-hint-bar'
import { ShareAnalyticsPanel } from '../../../../features/share-analytics/components/share-analytics-panel'
import { ShareLinkHero } from '../../../../features/share-analytics/components/share-link-hero'
import type { FounderProfileAnalyticsData } from '../../../../features/share-analytics/components/types'
import { VisitorsTable } from '../../../../features/share-analytics/components/visitors-table'
import {
  ShareProfileHeader,
  ShareProfileTabs,
} from '../../../../features/share-profile/components/share-profile-tabs'
import { VisibilityBanner } from '../../../../features/stealth-choice/components/visibility-banner'
import {
  openProfileChatPreviewStream,
  type StartupData,
  type StartupDocument,
  uploadDocument,
  useAnalyticsQuery,
  useCreateDocumentMutation,
  useCreateShareLinkMutation,
  useCreateTeamMemberMutation,
  useDeleteDocumentMutation,
  useDeleteShareLinkMutation,
  useDeleteTeamMemberMutation,
  useGetDocumentDownloadUrlMutation,
  useLinkStartupDocumentMutation,
  usePitchVideoUrlMutation,
  useProfileChatPreviewContextQuery,
  usePublicProfileFullQuery,
  usePublicProfilePreviewQuery,
  useRefreshTeamMemberLinkedInMutation,
  useShareLinksQuery,
  useStartupConfigQuery,
  useStartupDocumentsQuery,
  useTeamMembersQuery,
  useUnlinkStartupDocumentMutation,
  useUpdateShareLinkMutation,
  useUpdateStartupDocumentMutation,
  useUpdateStartupMutation,
  useUpdateTeamMemberMutation,
  useVerifyDocumentMutation,
} from '../../../../hooks/api'
import { useDocumentPreview } from '../../../../hooks/use-document-preview'
import { useVideoPreview } from '../../../../hooks/use-video-preview'
import { trackEvent } from '../../../../lib/gtag'

export const Route = createFileRoute('/app/startup/$id/profile')({
  validateSearch: (search: Record<string, unknown>): { focus?: string; linkId?: string } => ({
    focus: typeof search.focus === 'string' ? search.focus : undefined,
    linkId: typeof search.linkId === 'string' ? search.linkId : undefined,
  }),
  component: ProfileLayout,
})

/** Each page says what it is for. Share Links has its own header, see ShareProfileHeader. */
const PAGE_COPY: Record<Exclude<DataRoomTab, 'links'>, { title: string; sub?: string }> = {
  profile: {
    title: 'Profile',
  },
  'deep-insights': {
    title: FOUNDER_NAV_INVESTOR_FEEDBACK_LABEL,
    sub: 'What investors did once they opened your profile',
  },
  analytics: {
    title: FOUNDER_NAV_SHARE_ANALYTICS_LABEL,
  },
}

/** The sidebar leaf each page sits under, so the menu shows where the founder is. */
const ACTIVE_NAV: Record<DataRoomTab, string> = {
  profile: '',
  links: FOUNDER_NAV_SHARE_PROFILE_LABEL,
  'deep-insights': FOUNDER_NAV_INVESTOR_FEEDBACK_LABEL,
  analytics: FOUNDER_NAV_SHARE_ANALYTICS_LABEL,
}

// ── Which page is on screen ──
//
// These are three pages now, not three tabs — the sidebar routes between them.
// The layout still resolves which one it is, because the header and the
// completion strip differ per page.

function useActivePage(): DataRoomTab {
  const matchRoute = useMatchRoute()
  const params = Route.useParams()
  if (matchRoute({ to: '/app/startup/$id/profile/links', params })) return 'links'
  if (matchRoute({ to: '/app/startup/$id/profile/deep-insights', params })) return 'deep-insights'
  if (matchRoute({ to: '/app/startup/$id/profile/analytics', params })) return 'analytics'
  return 'profile'
}

// ── Mapper: backend → flat ProfileEditFormData ──

function toEditFormData(
  startup: StartupData,
  teamMembers: TeamMemberData[],
  docs: StartupDocument[]
): ProfileEditFormData {
  return {
    name: startup.name,
    sector: startup.categoryId ?? '',
    sectorLabel: startup.category?.name,
    stage: startup.productStage ?? '',
    location: startup.location ?? '',
    founded: startup.foundingDate ? new Date(startup.foundingDate).toISOString().slice(0, 7) : '',
    website: startup.websiteUrl ?? '',
    description: startup.description ?? '',
    tags: startup.tags,
    isFundraising: startup.isFundraising,
    fundraisingRound: startup.fundraisingStage ?? '',
    fundraisingTarget: startup.fundraisingAmount ?? '',
    team: teamMembers.map((m) => ({
      id: m.id,
      initials: getInitials(m.name),
      name: m.name,
      role: m.position ?? '',
      linkedInUrl: m.linkedinUrl ?? undefined,
      imageUrl: m.imageUrl,
      companyLogos: m.companyLogos,
      lastEducation: m.lastEducation,
      linkedinScrapeStatus: m.linkedinScrapeStatus,
    })),
    documents: docs.map((sd) => ({
      name: sd.label || sd.document.fileName,
      size: formatFileSize(sd.document.fileSize),
      documentId: sd.documentId,
      type: sd.type ?? null,
      // The deck is read as slide images; anything else needs a transcript.
      inChatContext: sd.type === 'PITCH_DECK' || sd.hasTranscript,
      uploadStatus: 'ready' as const,
      createdAt: sd.document.createdAt,
      mimeType: sd.document.mimeType,
      slideImageUrls: sd.slideImageUrls,
      deckAspectRatio: sd.deckAspectRatio,
    })),
  }
}

// ── Reverse mapper helpers ──

function parseFundraisingAmount(display: string): string | null {
  const cleaned = display.replace(/[$,\s]/g, '')
  if (!cleaned) return null
  const match = cleaned.match(/^(\d+(?:\.\d+)?)\s*([KkMmBb])?$/)
  if (!match || !match[1]) return cleaned
  const num = parseFloat(match[1])
  const suffix = (match[2] ?? '').toUpperCase()
  const multiplier =
    suffix === 'K' ? 1_000 : suffix === 'M' ? 1_000_000 : suffix === 'B' ? 1_000_000_000 : 1
  return Math.round(num * multiplier).toString()
}

// ── Layout component ──

function ProfileLayout() {
  const { startup, investorMatchesUnlocked, hasCompletedPitchSession } = useStartupContext()
  // Filled by the edit form while it is mounted; the header's Save and Cancel
  // call straight into it.
  const editActions = useRef<ProfileEditFormActions | null>(null)
  const [editStatus, setEditStatus] = useState({ isDirty: false, isSubmitting: false })
  const startupId = startup.id
  const navigate = useNavigate()
  const activeTab = useActivePage()

  // Config for edit form dropdowns
  const { data: configData } = useStartupConfigQuery()

  // Startup edit mutations
  const updateStartup = useUpdateStartupMutation(startupId)

  // Document queries and mutations
  const { data: startupDocs } = useStartupDocumentsQuery(startupId)
  const createDoc = useCreateDocumentMutation()
  const verifyDoc = useVerifyDocumentMutation()
  const deleteDoc = useDeleteDocumentMutation()
  const linkDoc = useLinkStartupDocumentMutation(startupId)
  const unlinkDoc = useUnlinkStartupDocumentMutation(startupId)
  const updateDoc = useUpdateStartupDocumentMutation(startupId)

  // Team member queries and mutations
  const { data: teamMembersData } = useTeamMembersQuery(startupId)
  const createTeamMember = useCreateTeamMemberMutation(startupId)
  const updateTeamMember = useUpdateTeamMemberMutation(startupId)
  const deleteTeamMember = useDeleteTeamMemberMutation(startupId)
  const refreshTeamMember = useRefreshTeamMemberLinkedInMutation(startupId)

  const persistedMemberIds = useMemo(
    () => new Set((teamMembersData ?? []).map((m) => m.id)),
    [teamMembersData]
  )

  const handleRefreshMember = useCallback(
    (memberId: string) => {
      refreshTeamMember.mutate({ path: { startupId, memberId } })
    },
    [refreshTeamMember, startupId]
  )

  // Analytics — used to populate per-link stats on the Share Links tab
  const { data: analyticsData } = useAnalyticsQuery(startupId)

  // Share link queries and mutations
  const { data: shareLinksData, isPending: shareLinksPending } = useShareLinksQuery(startupId)
  const createShareLink = useCreateShareLinkMutation(startupId)
  const updateShareLink = useUpdateShareLinkMutation(startupId)
  const deleteShareLink = useDeleteShareLinkMutation(startupId)

  const { focus, linkId } = Route.useSearch()
  const [profileMode, setProfileMode] = useState<'edit' | 'preview'>(focus ? 'edit' : 'preview')
  const [focusKey, setFocusKey] = useState<string | undefined>(focus)
  const [postImprovementReasons, setPostImprovementReasons] = useState<string[] | null>(null)
  const { canShowToday: canShowPostImprovementPrompt, markShownToday: markPostImprovementShown } =
    usePostImprovementPrompt(startup)

  // React to a `?focus=` param (e.g. from the profile completeness chips), even when
  // already mounted on this page: switch to edit mode, remember the target field, then
  // clear the param so re-selecting another chip fires again.
  useEffect(() => {
    if (!focus) return
    setProfileMode('edit')
    setFocusKey(focus)
    navigate({ to: '.', search: {}, replace: true })
  }, [focus, navigate])

  // Mapped form data
  const editFormData = useMemo(
    () => toEditFormData(startup, teamMembersData ?? [], startupDocs ?? []),
    [startup, teamMembersData, startupDocs]
  )

  // Preview data derived from flat form data
  const previewProfile = useMemo(
    () => ({
      initials: getInitials(editFormData.name),
      name: editFormData.name,
      sector: editFormData.sector,
      sectorLabel: editFormData.sectorLabel,
      stage: editFormData.stage,
      raisingStatus: editFormData.isFundraising,
      location: editFormData.location,
      founded: editFormData.founded,
      website: editFormData.website,
      description: editFormData.description,
      tags: editFormData.tags,
    }),
    [editFormData]
  )

  const previewFundraising = useMemo(
    () => ({
      round: editFormData.fundraisingRound,
      target: editFormData.fundraisingTarget,
    }),
    [editFormData]
  )

  // The deck the preview renders inline, matched the same way the share link
  // matches it: PITCH_DECK and a PDF, because only a PDF rasterises to pages.
  const previewPitchDeck = useMemo(
    () =>
      editFormData.documents.find(
        (doc): doc is typeof doc & { documentId: string } =>
          doc.type === 'PITCH_DECK' &&
          doc.mimeType === 'application/pdf' &&
          typeof doc.documentId === 'string' &&
          doc.documentId !== ''
      ),
    [editFormData.documents]
  )

  // The founder's own test drive of the chat. Authenticated, so it needs no
  // visitor token — and the route it posts to writes no turn row and no events,
  // which is what keeps the founder's own reading out of their analytics.
  const chatPreviewQuery = useProfileChatPreviewContextQuery(startupId, profileMode === 'preview')
  const chatPreviewContext = chatPreviewQuery.data ?? null
  const openChatPreviewStream = useCallback(
    (input: ProfileChatQuestionInput, signal: AbortSignal) =>
      openProfileChatPreviewStream(startupId, input, signal),
    [startupId]
  )

  // Profile preview data (evaluation, pitch recordings, video)
  const defaultToken = shareLinksData?.data?.find((l) => l.isDefault)?.token
  const profileQuery = usePublicProfileFullQuery(defaultToken ?? '', undefined, {
    enabled: !!defaultToken && profileMode === 'preview',
  })
  const videoUrlMutation = usePitchVideoUrlMutation()
  const pitchSessionId = profileQuery.data?.data?.pitchSessionId ?? null
  const video = useVideoPreview({
    fetchId: profileMode === 'preview' ? pitchSessionId : null,
    fetchVideoUrl: (id) =>
      videoUrlMutation.mutateAsync({ path: { pitchId: id } }).then((r) => r.data.url),
  })

  const founderAnalytics = analyticsData?.data as unknown as FounderProfileAnalyticsData | undefined

  const linkStatsMap = useMemo(() => {
    const map = new Map<string, { views: number; visitors: number; avgTime: string }>()
    const raw = analyticsData?.data as
      | {
          linkStats?: { id: string; views: number; visitors: number; avgTime: string }[]
        }
      | undefined
    for (const ls of raw?.linkStats ?? []) {
      map.set(ls.id, { views: ls.views, visitors: ls.visitors, avgTime: ls.avgTime })
    }
    return map
  }, [analyticsData])

  const shareLinks: ShareLinkConfig[] = useMemo(
    () =>
      (shareLinksData?.data ?? []).map((link) => {
        const stats = linkStatsMap.get(link.id)
        return {
          id: link.id,
          name: link.name,
          slug: link.token,
          domain: `${window.location.origin}/s`,
          isDefault: link.isDefault,
          isOutreach: link.isOutreach,
          enabled: link.enabled,
          includedInAnalytics: link.includedInAnalytics,
          chatEnabled: link.chatEnabled,
          requireEmailToView: link.requireEmailToView,
          allowDeckDownload: link.allowDeckDownload,
          visibilityConfig: link.visibilityConfig as ShareLinkConfig['visibilityConfig'],
          views: stats?.views ?? 0,
          investors: stats?.visitors ?? 0,
          avgTime: stats?.avgTime ?? '—',
          // Same month/day convention as the Visitors log's "Last visit"
          // column (share-link.repository.ts) — a creation date has no
          // meaningful time-of-day to show alongside it, though.
          createdAt: new Date(link.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
        }
      }),
    [shareLinksData, linkStatsMap]
  )
  const defaultShareLink = shareLinks.find((l) => l.isDefault) ?? shareLinks[0]

  // ── Edit handlers ──

  const handleUploadFiles = useCallback(
    async (files: File[]) => {
      const results = await Promise.all(
        files.map((file) => uploadDocument(file, createDoc, verifyDoc))
      )
      return results.map((r) => ({
        name: r.fileName,
        size: formatFileSize(r.fileSize),
        documentId: r.documentId,
        uploadStatus: 'ready' as const,
      }))
    },
    [createDoc, verifyDoc]
  )

  const handleDocumentsChange = useCallback(
    async (
      updatedDocs: ProfileEditFormData['documents']
    ): Promise<{ newDeckUploaded: boolean }> => {
      if (!startupDocs) return { newDeckUploaded: false }

      const existingDocIds = new Set(startupDocs.map((sd) => sd.documentId))
      const updatedDocIds = new Set(updatedDocs.map((d) => d.documentId).filter(Boolean))

      // Link newly uploaded documents first. A new deck supersedes the current one
      // on the server, so the old deck removed below is kept as an earlier version
      // instead of being deleted; removing a deck without a replacement still deletes it.
      let newDeckUploaded = false
      for (const doc of updatedDocs) {
        if (doc.documentId && !existingDocIds.has(doc.documentId)) {
          await linkDoc.mutateAsync({
            path: { startupId },
            body: {
              documentId: doc.documentId,
              label: doc.name,
              type: (doc.type as 'PITCH_DECK' | undefined) ?? undefined,
            },
          })
          if (doc.type === 'PITCH_DECK') newDeckUploaded = true
        }
      }

      // Unlink removed documents
      for (const sd of startupDocs) {
        if (!updatedDocIds.has(sd.documentId)) {
          await unlinkDoc.mutateAsync({
            path: { startupId, documentId: sd.documentId },
          })
          await deleteDoc.mutateAsync({ path: { id: sd.documentId } })
        }
      }

      // Update labels on existing documents
      for (const doc of updatedDocs) {
        if (doc.documentId && existingDocIds.has(doc.documentId)) {
          const existing = startupDocs.find((sd) => sd.documentId === doc.documentId)
          const currentLabel = existing?.label || existing?.document.fileName
          if (currentLabel !== doc.name) {
            await updateDoc.mutateAsync({
              path: { startupId, documentId: doc.documentId },
              body: { label: doc.name },
            })
          }
        }
      }

      return { newDeckUploaded }
    },
    [startupId, startupDocs, unlinkDoc, deleteDoc, linkDoc, updateDoc]
  )

  const handleTeamChange = useCallback(
    (updatedTeam: ProfileEditFormData['team']) => {
      if (!teamMembersData) return

      const existingIds = new Set(teamMembersData.map((m) => m.id))
      const updatedIds = new Set(updatedTeam.map((m) => m.id).filter(Boolean))

      const persist = async () => {
        for (const existing of teamMembersData) {
          if (!updatedIds.has(existing.id)) {
            await deleteTeamMember.mutateAsync({ path: { startupId, memberId: existing.id } })
          }
        }

        for (let i = 0; i < updatedTeam.length; i++) {
          const member = updatedTeam[i]
          if (!member) continue
          if (member.id && existingIds.has(member.id)) {
            await updateTeamMember.mutateAsync({
              path: { startupId, memberId: member.id },
              body: {
                name: member.name,
                position: member.role || null,
                linkedinUrl: member.linkedInUrl || null,
                order: i,
              },
            })
          } else {
            await createTeamMember.mutateAsync({
              path: { startupId },
              body: {
                name: member.name,
                position: member.role || undefined,
                linkedinUrl: member.linkedInUrl || null,
                order: i,
              },
            })
          }
        }
      }

      return persist()
    },
    [teamMembersData, createTeamMember, updateTeamMember, deleteTeamMember, startupId]
  )

  // Saves everything, then drops back to preview. Awaiting the whole set is what
  // lets the button show progress and the mode switch happen only once the data
  // is actually persisted.
  const handleFormSubmit = useCallback(
    async (formData: ProfileEditFormData) => {
      try {
        // Profile + fundraising + metric in one update
        await updateStartup.mutateAsync({
          path: { startupId },
          body: {
            name: formData.name,
            description: formData.description,
            location: formData.location || null,
            websiteUrl: formData.website || null,
            tags: formData.tags,
            isFundraising: formData.isFundraising,
            productStage: (formData.stage || null) as StartupData['productStage'],
            foundingDate: formData.founded || null,
            categoryId: formData.sector || null,
            fundraisingStage: formData.isFundraising ? formData.fundraisingRound || null : null,
            fundraisingAmount: formData.isFundraising
              ? parseFundraisingAmount(formData.fundraisingTarget)
              : null,
          },
        })

        await handleTeamChange(formData.team)
        const { newDeckUploaded } = await handleDocumentsChange(formData.documents)

        setProfileMode('preview')

        if (canShowPostImprovementPrompt()) {
          setPostImprovementReasons(
            newDeckUploaded ? ['New deck version uploaded', 'Profile updated'] : ['Profile updated']
          )
          markPostImprovementShown()
        }
      } catch (err) {
        // Stay in edit mode so the founder's unsaved work isn't lost.
        console.error('Failed to save profile:', err)
      }
    },
    [
      startupId,
      updateStartup,
      handleTeamChange,
      handleDocumentsChange,
      canShowPostImprovementPrompt,
      markPostImprovementShown,
    ]
  )

  // ── Share link handlers ──

  const handleAddShareLink = useCallback(
    (link: ShareLinkConfig) => {
      createShareLink.mutate({ path: { startupId }, body: { name: link.name } })
    },
    [startupId, createShareLink]
  )

  const handleUpdateShareLink = useCallback(
    (id: string, updates: Partial<ShareLinkConfig>) => {
      updateShareLink.mutate({
        path: { startupId, linkId: id },
        body: {
          name: updates.name,
          visibilityConfig: updates.visibilityConfig,
          enabled: updates.enabled,
          includedInAnalytics: updates.includedInAnalytics,
          chatEnabled: updates.chatEnabled,
          requireEmailToView: updates.requireEmailToView,
          allowDeckDownload: updates.allowDeckDownload,
        },
      })
    },
    [startupId, updateShareLink]
  )

  const handleDeleteShareLink = useCallback(
    (id: string) => {
      deleteShareLink.mutate({ path: { startupId, linkId: id } })
    },
    [startupId, deleteShareLink]
  )

  // ── Document preview ──

  const documentDownloadMutation = useGetDocumentDownloadUrlMutation()
  const docPreview = useDocumentPreview({
    fetchDocumentUrl: async (documentId) => {
      const res = await documentDownloadMutation.mutateAsync({ path: { id: documentId } })
      if (res.status === 'ok') return res.data.downloadUrl
      throw new Error('Failed to get download URL')
    },
  })

  return (
    <FounderLayout activeNav={ACTIVE_NAV[activeTab]}>
      <div className="mx-auto max-w-6xl py-2">
        {activeTab === 'links' ? (
          <ShareProfileHeader />
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {PAGE_COPY[activeTab].title}
              </h1>
              {PAGE_COPY[activeTab].sub && (
                <p className="mt-1 text-sm text-muted-foreground">{PAGE_COPY[activeTab].sub}</p>
              )}
            </div>
            {/* One row of actions, whichever mode we are in. While editing it is
              the form's own Save and Cancel — called through the ref, so there
              is no second implementation of saving to drift. */}
            {activeTab === 'profile' &&
              (profileMode === 'edit' ? (
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  <SecondaryButton size="sm" onClick={() => editActions.current?.discard()}>
                    Cancel
                  </SecondaryButton>
                  <CtaButton
                    size="sm"
                    loading={editStatus.isSubmitting}
                    onClick={() => editActions.current?.submit()}
                  >
                    {editStatus.isSubmitting ? 'Saving…' : 'Save changes'}
                  </CtaButton>
                </div>
              ) : (
                <ProfileActions
                  startupId={startupId}
                  shareLinks={shareLinks}
                  shareLinksPending={shareLinksPending}
                  deckDocumentId={previewPitchDeck?.documentId}
                  onEdit={() => setProfileMode('edit')}
                  onDeckUploaded={() => {
                    if (canShowPostImprovementPrompt()) {
                      setPostImprovementReasons(['New deck version uploaded'])
                      markPostImprovementShown()
                    }
                  }}
                />
              ))}
          </div>
        )}

        {/* Only while completing the profile still unlocks something. Once the
            matched investors are unlocked it is a nag with nothing behind it —
            Home carries whatever the next step is. */}
        {activeTab === 'profile' && !investorMatchesUnlocked && (
          <div className="mt-6">
            <ProfileCompletenessCard startupId={startupId} />
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="relative mt-6">
            {profileMode === 'edit' ? (
              <ProfileEditForm
                key={focusKey ?? 'profile-edit'}
                data={editFormData}
                onSubmit={handleFormSubmit}
                // The only way out of edit mode now that the header's actions
                // hide while editing.
                // The header carries Save/Cancel, so the form's own sticky bar
                // stands down rather than offering a second pair.
                hideActionBar
                actionsRef={editActions}
                onStatusChange={setEditStatus}
                onDiscard={() => setProfileMode('preview')}
                onUploadFiles={handleUploadFiles}
                onPreviewDocument={(doc) => {
                  if (doc.documentId) {
                    docPreview.openDocument({
                      id: doc.documentId,
                      fileName: doc.name,
                      mimeType: doc.mimeType ?? null,
                    })
                  }
                }}
                initialFocus={focusKey}
                categoryOptions={configData?.categories?.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                stageOptions={configData?.productStages}
                raiseStageOptions={configData?.raiseStages}
                persistedMemberIds={persistedMemberIds}
                onRefreshMember={handleRefreshMember}
                refreshingMemberId={
                  refreshTeamMember.isPending
                    ? (refreshTeamMember.variables?.path.memberId ?? null)
                    : null
                }
              />
            ) : (
              <AskProfileProvider
                // The founder previewing their own profile. Every investor
                // conversion surface is withheld — see AskProfileAudience.
                audience="founder"
                openStream={openChatPreviewStream}
                startupName={startup.name}
                sources={chatPreviewContext?.sources ?? []}
                slideCount={chatPreviewContext?.slideCount ?? 0}
                documentCount={chatPreviewContext?.documentCount ?? 0}
                // The founder is not a lead. The doors and the founder-contact
                // path exist for investors, so here they are inert rather than
                // rendered and dead.
                onDoorSubmit={() => undefined}
                onConnectFounder={() => undefined}
                onTrackEvent={() => undefined}
                // The preview writes no server row for this to read back on
                // reload — see AskProfileProviderProps.localHistoryKey — so
                // the whole turn is kept in this browser instead.
                localHistoryKey={startup.id}
              >
                <ProfileView
                  pitchRecordingEmptyAction={
                    hasCompletedPitchSession ? undefined : (
                      <Link
                        to="/app/startup/$id/pitch/start"
                        params={{ id: startupId }}
                        className={ctaLinkClasses({ size: 'sm' })}
                      >
                        <Sparkles />
                        <span>Complete AI session</span>
                      </Link>
                    )
                  }
                  profile={previewProfile}
                  fundraising={previewFundraising}
                  pitchRecordings={
                    profileQuery.data?.data?.evaluation && profileQuery.data?.data?.pitchSessionId
                      ? [
                          {
                            id: profileQuery.data.data.pitchSessionId,
                            duration: profileQuery.data.data.evaluation.pitchDuration || '30 min',
                            readinessLabel: '',
                            recordedDate: String(profileQuery.data.data.evaluation.date),
                            verdictSnippet: '',
                            highlights: [],
                            judgeCount: 0,
                            topStrength: '',
                            topConcern: '',
                          },
                        ]
                      : []
                  }
                  selectedPitchId={profileQuery.data?.data?.pitchSessionId ?? ''}
                  // The deck renders inline here for the same reason it does on
                  // the share link: this tab is what the founder is judging the
                  // link by, and a click-to-open CTA is not what an investor
                  // sees. Without it the preview shows a different page from the
                  // one being previewed.
                  deckPreview={
                    previewPitchDeck ? (
                      <InlineDocumentPreview
                        documentId={previewPitchDeck.documentId}
                        fetchUrl={docPreview.fetchDocumentUrl}
                        hotkeysEnabled={!docPreview.previewOpen && !video.videoOpen}
                        placeholderImageUrls={previewPitchDeck.slideImageUrls}
                        initialAspectRatio={previewPitchDeck.deckAspectRatio}
                      />
                    ) : undefined
                  }
                  documents={editFormData.documents}
                  team={editFormData.team}
                  startupName={startup.name}
                  videoUrl={video.videoUrl ?? undefined}
                  onPlayVideo={video.handlePlayRecording}
                  onDocumentClick={(doc) => {
                    if (doc.documentId) {
                      docPreview.openDocument({
                        id: doc.documentId,
                        fileName: doc.name,
                        mimeType: doc.mimeType ?? null,
                      })
                    }
                  }}
                  chatWidget={chatPreviewContext ? <AskProfileSection /> : undefined}
                />
              </AskProfileProvider>
            )}
          </div>
        )}
        {activeTab === 'analytics' && (
          <div className="mt-6 space-y-6">
            <ShareAnalyticsPanel
              analytics={founderAnalytics}
              isLoading={!analyticsData}
              link={defaultShareLink}
            />
            {founderAnalytics && founderAnalytics.visitors.length > 0 && (
              <>
                <FeedbackHintBar startupId={startupId} />
                <VisitorsTable
                  visitors={founderAnalytics.visitors}
                  onLinkClick={(clickedLinkId) =>
                    navigate({ to: '.', search: { linkId: clickedLinkId }, replace: true })
                  }
                />
              </>
            )}
          </div>
        )}
        {activeTab === 'links' && (
          <div className="mt-6 space-y-6">
            <ShareProfileTabs startupId={startupId} active="share-links" />
            {/* This page answers "who can see my profile", so the visibility
                state is its headline, not a banner repeated on every page. */}
            <VisibilityBanner startupId={startupId} isStealth={startup.isStealth} />
            <ShareLinkHero
              link={defaultShareLink}
              deckDocumentId={previewPitchDeck?.documentId}
              notificationPreference={startup.investorViewNotificationPreference}
              onNotificationPreferenceChange={(pref) =>
                updateStartup.mutate({
                  path: { startupId },
                  body: { investorViewNotificationPreference: pref },
                })
              }
            />
            <ShareLinks
              links={shareLinks}
              documents={editFormData.documents}
              focusLinkId={linkId}
              isLoading={shareLinksPending}
              onAddLink={handleAddShareLink}
              onUpdateLink={handleUpdateShareLink}
              onDeleteLink={handleDeleteShareLink}
              onLinkCopied={(copiedLinkId) =>
                trackEvent('dataroom_link_copied', {
                  startup_id: startupId,
                  is_first: shareLinks.length === 1 && shareLinks[0]?.id === copiedLinkId,
                })
              }
            />
          </div>
        )}
        {activeTab === 'deep-insights' && (
          <div className="mt-6 space-y-10">
            <AiReadsPanel startupId={startupId} />
            <DeepInsightsPanel startupId={startupId} />
          </div>
        )}
      </div>
      <Outlet />
      <DocumentPreviewModal
        document={docPreview.previewDoc}
        open={docPreview.previewOpen}
        onOpenChange={docPreview.setPreviewOpen}
        fetchUrl={docPreview.fetchDocumentUrl}
      />
      {video.videoUrl && (
        <VideoPreviewModal
          videoUrl={video.videoUrl}
          title="Pitch Recording"
          open={video.videoOpen}
          onOpenChange={video.setVideoOpen}
          startTime={video.videoStartTime}
          onTimeUpdate={video.setVideoResumeTime}
        />
      )}
      {postImprovementReasons && (
        <PostImprovementPromptOverlay
          reasons={postImprovementReasons}
          shareUrl={defaultShareLink ? `${defaultShareLink.domain}/${defaultShareLink.slug}` : ''}
          onDismiss={() => setPostImprovementReasons(null)}
        />
      )}
    </FounderLayout>
  )
}

/**
 * The things a founder comes to this page to do.
 *
 * They were previously spread between a floating Edit button, the edit form's
 * innards, and nowhere at all — "upload a new pitch deck" had no entry point,
 * which is exactly what founders kept asking for. Document upload lives inside
 * the edit form now, not as its own shortcut — "Edit your profile" reaches it.
 */
function ProfileActions({
  startupId,
  shareLinks,
  shareLinksPending,
  deckDocumentId,
  onEdit,
  onDeckUploaded,
}: {
  startupId: string
  shareLinks: ShareLinkConfig[]
  shareLinksPending: boolean
  deckDocumentId: string | undefined
  onEdit: () => void
  onDeckUploaded: () => void
}) {
  // Same upload path as Home's dropzone: upload and verify the new deck before
  // the old one is removed, so a failed upload cannot leave a startup deckless.
  const deck = useDeckDrop(startupId, onDeckUploaded)

  return (
    <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
      {/* Neither "open pitch deck" nor "complete AI session" live here: the
          profile card below carries both, each next to the thing it fills in.
          All three share the DSM button components so they match in height
          and weight — a hand-rolled secondary drifted a few pixels shorter. */}
      <input {...deck.getInputProps()} />
      <SecondaryButton size="sm" icon={<FileUp />} loading={deck.isUploading} onClick={deck.open}>
        {deck.isUploading ? 'Uploading…' : 'Upload new pitch deck'}
      </SecondaryButton>
      <SecondaryButton size="sm" icon={<Pencil />} onClick={onEdit}>
        Edit your profile
      </SecondaryButton>
      <ShareButton
        startupId={startupId}
        shareLinks={shareLinks}
        pending={shareLinksPending}
        deckDocumentId={deckDocumentId}
      />
      {deck.error && <p className="w-full text-right text-[12.5px] text-red-400">{deck.error}</p>}
    </div>
  )
}

/**
 * Share always opens the "Your links" panel, even with a single link, so the
 * URL, deck preview, and settings link are visible before copying — Default
 * plus the three most recently created, searchable past five.
 */
function ShareButton({
  startupId,
  shareLinks,
  pending,
  deckDocumentId,
}: {
  startupId: string
  shareLinks: ShareLinkConfig[]
  pending: boolean
  deckDocumentId: string | undefined
}) {
  const defaultLink = shareLinks.find((l) => l.isDefault) ?? shareLinks[0]
  const [selectedId, setSelectedId] = useState(defaultLink?.id)
  const [query, setQuery] = useState('')
  const [copied, setCopied] = useState(false)
  const [open, setOpen] = useState(false)

  const selectedLinkForPreview = shareLinks.find((l) => l.id === selectedId) ?? defaultLink
  // Same slide-1 image the /s/ link's og:image uses — the token is the share
  // link's slug, so this reuses the public preview endpoint, no new one needed.
  const preview = usePublicProfilePreviewQuery(selectedLinkForPreview?.slug ?? '').data?.data

  const selectedLink = shareLinks.find((l) => l.id === selectedId) ?? defaultLink
  const deckShared = isDeckShared(selectedLink?.visibilityConfig, deckDocumentId)
  const shareUrl = selectedLink ? `${selectedLink.domain}/${selectedLink.slug}` : null

  const copyLink = (link: ShareLinkConfig) => {
    navigator.clipboard.writeText(`${link.domain}/${link.slug}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    trackEvent('dataroom_link_copied', {
      startup_id: startupId,
      is_first: shareLinks.length === 1,
    })
  }

  // Default first, then most recently created — an approximation of "most
  // recently used" that needs no new field. A search narrows across every
  // link, Default included, not just the recent slice — typing nonsense
  // hides Default too and shows no results, same as any other link.
  const others = shareLinks
    .filter((l) => l.id !== defaultLink?.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const rows = query
    ? shareLinks.filter((l) => l.name.toLowerCase().includes(query.toLowerCase()))
    : defaultLink
      ? [defaultLink, ...others.slice(0, 3)]
      : others.slice(0, 3)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <CtaButton size="sm" icon={<Share2 />}>
          Share your Profile
        </CtaButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[440px] p-4">
        <p className="mb-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          Your links
        </p>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search links or type an investor name…"
          className="mb-2 w-full rounded-lg border border-border bg-card2 px-2.5 py-2 text-xs text-foreground placeholder:text-muted-foreground"
        />
        {pending && <SpinnerRow className="py-3 text-xs">Loading your links…</SpinnerRow>}
        {!pending && rows.length === 0 && (
          <p className="py-3 text-center text-xs text-muted-foreground">No links match "{query}"</p>
        )}
        <div>
          {rows.map((link) => (
            <button
              key={link.id}
              type="button"
              onClick={() => setSelectedId(link.id)}
              className="flex w-full items-center gap-3 border-b border-border/60 py-2.5 text-left last:border-b-0"
            >
              <span
                className={`size-3.5 shrink-0 rounded-full border-2 ${
                  selectedLink?.id === link.id
                    ? 'border-primary bg-primary'
                    : 'border-border bg-transparent'
                }`}
              />
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-foreground">
                {link.name}
              </span>
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {link.isDefault ? `${link.views} views` : 'recent'}
              </span>
            </button>
          ))}
        </div>
        {!pending && (
          <Link
            to="/app/startup/$id/profile/links"
            params={{ id: startupId }}
            onClick={() => setOpen(false)}
            className="block border-t border-border/60 py-2.5 text-center text-xs text-muted-foreground hover:text-foreground"
          >
            All {shareLinks.length} links →
          </Link>
        )}
        {shareUrl && selectedLink && rows.length > 0 && (
          <>
            <div className="mt-2.5 flex items-center gap-2.5 rounded-[10px] border border-border bg-card2 px-3.5 py-2.5 text-[14.5px]">
              <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground">
                {selectedLink.domain}/
                <span className="font-semibold text-foreground">{selectedLink.slug}</span>
              </span>
              <button
                type="button"
                onClick={() => copyLink(selectedLink)}
                className="shrink-0 rounded-lg bg-primary px-3.5 py-[7px] text-xs font-bold text-primary-foreground"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {!deckShared ? null : preview?.deckPreviewImageUrl ? (
              <img
                src={preview.deckPreviewImageUrl}
                alt="First slide of your pitch deck"
                className="mt-2.5 h-auto w-full rounded-xl border border-border"
              />
            ) : (
              <div
                className="mt-2.5 grid h-[118px] place-items-center overflow-hidden rounded-xl border border-border"
                style={{
                  background: 'linear-gradient(135deg, #1E1105, #3A1D07 55%, #F97316 190%)',
                }}
              >
                <span className="text-xs font-bold tracking-wide text-primary">Deck · page 1</span>
              </div>
            )}
            <Link
              to="/app/startup/$id/profile/links"
              params={{ id: startupId }}
              search={{ linkId: selectedLink.id }}
              onClick={() => setOpen(false)}
              className="mt-2.5 block text-xs text-primary hover:underline"
            >
              Share settings &amp; analytics →
            </Link>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
